import React from 'react';
import PropTypes from 'prop-types';

import SortingIcon from "../table-common/SortingIcon";
import ItemStatus from "../table-common/ItemStatus";

import {getHighlightedText} from "../table-common/table-search";
import {columnsByType, reduceWidthWithXCiColumns} from './table-configuration';
import {getUniqueConditionNames} from "./condition-processing";

const TableHeader = ({tab, uniqueConditions, sortingBy, sortReversed, changeSort, searchBy}) => {
    const columns = columnsByType[tab];
    const mapToHeaderColumn = (conditionName, index) => {
        return (
            <th key={`column-condition-${index}`} onClick={() => changeSort(`ci:${conditionName}`)}
                title={conditionName}>
                {getHighlightedText(conditionName, searchBy)}
                <SortingIcon visible={sortingBy === `ci:${conditionName}`} reversed={sortReversed}/>
            </th>
        );
    };

    return (
        <thead>
        <tr>
            {columns.status ? (
                <th onClick={() => changeSort('status', false)}>
                    <i className="fa fa-exclamation-triangle"/> <SortingIcon
                    visible={sortingBy === 'status'} reversed={!sortReversed /* This icon is different from others */}/>
                </th>
            ) : ''}
            {columns.car ? (
                <th onClick={() => changeSort('car', false)}>
                    Car <SortingIcon
                    visible={sortingBy === 'car'} reversed={sortReversed}/>
                </th>
            ) : ''}
            {columns.bogie ? (
                <th onClick={() => changeSort('bogie', false)}>
                    Bogie <SortingIcon
                    visible={sortingBy === 'bogie'} reversed={sortReversed}/>
                </th>
            ) : ''}
            {columns.asset ? (
                <th onClick={() => changeSort('asset', false)}>
                    Asset <SortingIcon
                    visible={sortingBy === 'asset'} reversed={sortReversed}/>
                </th>
            ) : ''}
            {columns.lastDataTimestamp ? (
                <th onClick={() => changeSort('lastDataTimestamp')}>
                    Data received <SortingIcon
                    visible={sortingBy === 'lastDataTimestamp'} reversed={sortReversed}/>
                </th>
            ) : ''}
            {uniqueConditions.map(mapToHeaderColumn)}
        </tr>
        </thead>
    )
}

TableHeader.propTypes = {
    uniqueConditions: PropTypes.array,
    sortingBy: PropTypes.string,
    sortReversed: PropTypes.bool,
    changeSort: PropTypes.func,
    searchBy: PropTypes.string
}

const TableRow = ({tab, element, uniqueConditions, searchBy, openConditionModal, seeCarView, goToCar}) => {
    const columns = columnsByType[tab];
    // Access to Angular filters from ekeFilters.js
    const injector = angular.element(".ng-scope").injector();
    const timestampFormatter = injector.get('secondsAgoUTCFilter');

    const mapToValueColumn = (conditionName, index) => {
        const condition = element.conditions.find(condition => condition.name === conditionName);
        if (condition) {
            let conditionColor = "";
            if (condition.status === 'WARNING') conditionColor = 'cell-yellow';
            if (condition.status === 'ALARM') conditionColor = 'cell-red';

            const value = condition.value;
            const unit = ' ' + condition.unit;

            return (<td key={`condition-${index}`}
                        title={value + unit}
                        className={conditionColor}
                        role="button"
                        onClick={openConditionModal(element.asset, condition.ci_value)}>
                {value}{uniqueConditions.length < reduceWidthWithXCiColumns ? unit : ''}</td>);
        } else {
            return (<td key={`condition-${index}`}>.</td>);
        }
    };

    return (
        <tr>
            {columns.status ? (<td>{<ItemStatus status={element.status}/>}</td>) : undefined}
            {columns.car ? (
              <td>
                  {seeCarView && <button className="link" onClick={() => goToCar(element.car)}>
                      {getHighlightedText(element.car, searchBy)}
                  </button>}
                  {!seeCarView && <span>{getHighlightedText(element.car, searchBy)}</span>}
              </td>
            ) : undefined}
            {columns.bogie ? (<td>{getHighlightedText(element.bogie, searchBy)}</td>) : undefined}
            {columns.asset ? (<td>{getHighlightedText(element.wheel, searchBy)}</td>) : undefined}
            {columns.lastDataTimestamp ? (<td title={element.lastDataTimestamp}>
                {timestampFormatter(element.lastDataTimestamp)}</td>) : undefined}
            {uniqueConditions.map(mapToValueColumn)}
        </tr>
    )
};
TableRow.propTypes = {
    element: PropTypes.object,
    uniqueConditions: PropTypes.array,
    searchBy: PropTypes.string,
    openModal: PropTypes.func
}

const DataTable = ({tab, data, searchBy, sorting, $uibModal, seeCarView, goToCar}) => {
    const openConditionModal = (asset, ci_value) => () => {
        if (!ci_value) return;
        $uibModal.open({
            component: 'conditionGraphModal',
            windowClass: 'subSystemWindow',
            resolve: {
                alert: function () {
                    // Condition graph modal opens condition-indicators-list that uses
                    // asset for asset.id in request for the graph data
                    // ciType for warning and alert threshold line configuration
                    return {
                        asset: asset,
                        ciType: {...ci_value.config, ...ci_value}
                    };
                },
            },
        }).result.then(() => {}, () => {})
    }

    const uniqueConditions = getUniqueConditionNames(data);

    let tableContent = (<tr>
        <td>No {searchBy ? "matching" : ""} data</td>
    </tr>);
    if (data.length) {
        tableContent = data.map((element, index) =>
            (<TableRow
                    tab={tab}
                    key={`table-row-${index}`}
                    element={element}
                    uniqueConditions={uniqueConditions}
                    searchBy={searchBy}
                    openConditionModal={openConditionModal}
                    seeCarView={seeCarView}
                    goToCar={goToCar}/>
            ));
    }

    return (
        <div className="data-table">
            <table aria-label="CI condition latest values">
                <TableHeader
                    tab={tab}
                    uniqueConditions={uniqueConditions}
                    sortingBy={sorting.sortingBy}
                    sortReversed={sorting.sortReversed}
                    changeSort={sorting.changeSort}
                    searchBy={searchBy}
                />
                <tbody>{tableContent}</tbody>
            </table>
        </div>
    );
};

DataTable.propTypes = {
    data: PropTypes.array,
    searchBy: PropTypes.string,
    sorting: PropTypes.object
};

export default DataTable;